import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";
import { service } from "../services/AxiosServices";

interface ChatContextProps {
  chats: Task[];
  setChats: React.Dispatch<React.SetStateAction<Task[]>>;
  chatHistory: ChatHistoryEntry[];
  setChatHistory: React.Dispatch<React.SetStateAction<ChatHistoryEntry[]>>;
  fetchChatHistory: (Id: string, spaceChat: boolean) => void;
  loading: boolean;
  chatStates: Record<string, ChatState>;
  setChatStates: React.Dispatch<React.SetStateAction<Record<string, ChatState>>>;
  currentSectionId: string | null;
  setCurrentSectionId: React.Dispatch<React.SetStateAction<string | null>>;
  isProcessingRequest: boolean;
  setIsProcessingRequest: React.Dispatch<React.SetStateAction<boolean>>;
  saveCurrentState: () => void; 
  selectedLLMs: number[];
  setSelectedLLMs: React.Dispatch<React.SetStateAction<number[]>>;
  feedback: number;
  setFeedback: React.Dispatch<React.SetStateAction<number>>;
}

interface ChatState {
  input: string;
  pendingMessage: string | null;
  isLoading: boolean;
  chats?: Task[]; 
  selectedLLMs: number[];
  feedback: number; 
}

interface ModelResponse {
  model_name: string;
  response: string;
}

interface Task {
  id: number;
  task_description: string;
  response: ModelResponse[];
  section_id?: string;
  selectedModels?: number[];
  streaming?: boolean;
  partialResponses?: {[key: string]: string};
}

interface ChatHistoryEntry {
  id: string;
  desc: string | null;
}

// Creating the ChatContext with undefined as default value
const ChatContext = createContext<ChatContextProps | undefined>(undefined);

// Custom hook to access the ChatContext
export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChatContext must be used within a ChatProvider");
  }
  return context;
};

export const ChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [chats, setChats] = useState<Task[]>([]);
  const [chatHistory, setChatHistory] = useState<ChatHistoryEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isProcessingRequest, setIsProcessingRequest] = useState<boolean>(false);
//  state variables to manage chat states across sections
  const [chatStates, setChatStates] = useState<Record<string, ChatState>>({});
  const [currentSectionId, setCurrentSectionId] = useState<string | null>(null);
  const [selectedLLMs, setSelectedLLMs] = useState<number[]>([]);
  const [feedback, setFeedback] = useState<number>(0);

  // Function to save current chat state
  const saveCurrentState = useCallback(() => {
    if (currentSectionId) {
      setChatStates(prev => ({
        ...prev,
        [currentSectionId]: {
          input: "", 
          pendingMessage: null,
          isLoading: false,
          chats: chats.filter(chat => chat.section_id === currentSectionId),
          selectedLLMs: selectedLLMs,
          feedback: feedback
        }
      }));
    }
  }, [currentSectionId, chats, selectedLLMs, feedback]);

  // Function to fetch chat history based on ID and whether it's a space chat
  const fetchChatHistory = useCallback(async (Id: string, spaceChat: boolean) => {
    try {
      setLoading(true);
      setChatHistory([]);
      
      const endPoint = spaceChat ? "api/get-ai-space-chat-history" : "/api/getchathistory";
  
      const requestData = {
        ...(spaceChat ? { space_id: Id } : { crew: Id }),
        ...(spaceChat ? {} : { project_id: localStorage.getItem('project_id') }),
        type: spaceChat ? undefined : 1,
      };
  
      const response = await service<{ sections: Record<string, string | null> | 'No history' }>(
        endPoint,
        "post",
        requestData
      );
      
      if (response.data.sections === 'No history') {
        setChatHistory([{ id: 'history', desc: 'No history' }]);
        return; 
      }
  
      const history = Object.entries(response.data.sections)
        .map(([key, value]) => ({
          id: key,
          desc: value as string | null,
        }))
        .sort((a, b) => Number(b.id) - Number(a.id));
        
      console.log('chat history', history);
  
      setChatHistory(history);
    } catch (err) {
      console.error("Error fetching chat history:", err);
    } finally {
      setLoading(false); 
    }
  }, []);
  
  return (
    <ChatContext.Provider 
      value={{ 
        chats, 
        setChats, 
        chatHistory, 
        setChatHistory, 
        fetchChatHistory, 
        loading,
        chatStates,
        setChatStates,
        currentSectionId,
        setCurrentSectionId,
        isProcessingRequest,
        setIsProcessingRequest,
        saveCurrentState,
        selectedLLMs,
        setSelectedLLMs,
        feedback,
        setFeedback
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};